import axios, { type AxiosInstance } from 'axios';

/**
 * Pick the API base URL based on where the SPA is being served from:
 *  - admin.<domain> / portal.<domain> → calls hop sideways to api.<domain>
 *  - localhost:5173 (dev)             → empty baseURL, Vite proxies to Herd
 * Override with VITE_API_URL=... when building for an environment whose API
 * isn't at api.<same-host>.
 */
function detectBaseURL(): string {
  const override = import.meta.env.VITE_API_URL;
  if (override) return override;

  const host = window.location.host;
  const match = host.match(/^(admin|portal)\.(.+)$/);
  if (match) {
    return `${window.location.protocol}//api.${match[2]}`;
  }
  return '';
}

const baseURL = detectBaseURL();

/** Absolute URL on the API host — for `<audio>`/`<img>` src that can't use axios. */
export function apiUrl(path: string): string {
  return `${baseURL}${path}`;
}

export const api: AxiosInstance = axios.create({
  baseURL,
  withCredentials: true,
  withXSRFToken: true,
  headers: {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
});

/**
 * Manually read the XSRF-TOKEN cookie and copy it into the X-XSRF-TOKEN header
 * on every outgoing request. axios is supposed to do this when withXSRFToken is
 * true, but its detection of "cross-origin" can miss in subdomain SPA setups —
 * forcing it here makes the Sanctum/Fortify cross-subdomain handshake reliable.
 */
function readXsrfCookie(): string | null {
  const match = document.cookie
    .split('; ')
    .find((c) => c.startsWith('XSRF-TOKEN='));
  if (!match) return null;
  return decodeURIComponent(match.slice('XSRF-TOKEN='.length));
}

api.interceptors.request.use((config) => {
  const token = readXsrfCookie();
  if (token) {
    config.headers.set('X-XSRF-TOKEN', token);
  }
  return config;
});

let csrfPrimed = false;

/**
 * Sanctum CSRF cookie must be set before any state-changing request.
 * Call once on app boot or on demand from a mutation hook.
 */
export async function primeCsrf(): Promise<void> {
  if (csrfPrimed) {
    return;
  }
  await api.get('/sanctum/csrf-cookie');
  csrfPrimed = true;
}
